import { Card, CardContent, Stack } from '@mui/joy';
import { isEqual } from 'lodash';
import { useFieldArray, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  AttributeValueRows,
  ColourEditor,
  getAttributeValueKey,
  IAttributeValueField,
  NumberRangeEditor,
  ValuesEditor,
} from 'companySettings/components/PartFinderAttributeValues';
import { AttributeStatus, AttributeType } from 'companySettings/enums';
import { IPartFinderAttribute, IPartFinderAttributeValue } from 'companySettings/types';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { ISubmitAction } from 'core/types';
import { Color, Variant } from 'theme/enums';

import { IUpsertPartFinderAttributeFormData } from './types';
import { PartFinderAttributeVersionHistory } from '../VersionHistory';

interface IProps {
  attribute?: IPartFinderAttribute;
  isOpen: boolean;
  onClose: (reset?: UseFormReset<IUpsertPartFinderAttributeFormData>) => void;
  onSubmit: (
    status: AttributeStatus
  ) => (
    reset: UseFormReset<IUpsertPartFinderAttributeFormData>
  ) => (data: IUpsertPartFinderAttributeFormData) => Promise<void>;
  // Saved and about to close: nothing more can be submitted.
  isSaved?: boolean;
}

export const UpsertPartFinderAttributeFormFields = ({
  attribute,
  isOpen,
  onClose,
  onSubmit,
  isSaved = false,
}: IProps) => {
  const { t } = useTranslation();

  const { control, errors, watch, setError } = useFormContext();
  const { fields, append, move, remove } = useFieldArray({ control, name: 'values' });

  const [name, type, values]: [string, AttributeType, IPartFinderAttributeValue[]] = watch([
    'name',
    'type',
    'values',
  ]);
  const valueFields = fields as IAttributeValueField[];
  const hasValues = valueFields.length > 0;

  const hasChanges =
    !attribute || name?.trim() !== attribute.name || !isEqual(values ?? [], attribute.values ?? []);
  // A saved draft can be published as it is; a published list needs a change first.
  const hasUnpublishedDraft = Boolean(attribute) && AttributeStatus.PUBLISHED !== attribute.status;

  const typeOptions = [
    { value: AttributeType.VALUES, label: t('labelAttributeTypeValues') },
    { value: AttributeType.COLOUR, label: t('labelAttributeTypeColour') },
    { value: AttributeType.NUMBER_RANGE, label: t('labelAttributeTypeNumberRange') },
  ];

  const onAddValue = (value: IPartFinderAttributeValue) => {
    const valueKey = getAttributeValueKey(type, value);

    if (valueFields.some((field) => valueKey === getAttributeValueKey(type, field))) {
      return false;
    }

    append(value);

    return true;
  };

  const getSubmitActions = (): ISubmitAction[] => [
    {
      type: AttributeStatus.PUBLISHED,
      label: t('buttonLabelSaveAndPublish'),
      disabled: isSaved || (!hasChanges && !hasUnpublishedDraft),
      onSubmit: (reset) => (data: IUpsertPartFinderAttributeFormData) => {
        if (!data?.values?.length) {
          setError('values', {
            type: 'min',
            message: t('errorPartFinderAttributeValuesRequired'),
          });

          return;
        }

        return onSubmit(AttributeStatus.PUBLISHED)(reset)(data);
      },
    },
    {
      type: AttributeStatus.DRAFT,
      label: t('buttonLabelSaveAsDraft'),
      disabled: isSaved || !hasChanges,
      // Enter saves a draft; publishing always takes the button.
      isDefault: true,
      onSubmit: onSubmit(AttributeStatus.DRAFT),
    },
  ];

  // The pill's Version History popover renders outside the drawer, so the pill only exists while
  // the drawer is open: closing the drawer then takes the popover with it.
  const versionHistory =
    attribute && isOpen ? <PartFinderAttributeVersionHistory attribute={attribute} /> : undefined;

  return (
    <Form
      title={attribute ? t('titlePartFinderAttributes') : t('titleAddPartFinderAttribute')}
      subtitle={versionHistory}
      isOpen={isOpen}
      onClose={onClose}
      getSubmitActions={getSubmitActions}
      cancelBtnLabel={t('buttonLabelCancel')}
      disableAutoFocus={Boolean(attribute)}
    >
      <FormField
        name="name"
        type={InputType.TEXT_INPUT}
        inputProps={{ label: t('labelName'), required: true }}
      />

      <Card
        variant={Variant.OUTLINED}
        color={Color.PRIMARY}
        sx={{ backgroundColor: 'neutral.100' }}
      >
        <CardContent>
          <Stack gap={2}>
            <FormField
              name="type"
              type={InputType.SELECT}
              inputProps={{
                control,
                error: errors?.type,
                label: t('labelType'),
                required: true,
                // A saved list keeps its type. Before the first save, switching type would leave
                // values of the wrong kind behind, so they have to be removed first.
                disabled: Boolean(attribute) || hasValues,
                options: typeOptions,
                getValue: (option) => option.value,
                getLabel: (option) => option.label,
              }}
            />

            {AttributeType.VALUES === type && <ValuesEditor onAdd={onAddValue} />}

            {AttributeType.COLOUR === type && <ColourEditor onAdd={onAddValue} />}

            {hasValues && (
              <AttributeValueRows
                values={valueFields}
                type={type}
                onMove={move}
                onRemove={remove}
              />
            )}

            {AttributeType.NUMBER_RANGE === type && (
              <NumberRangeEditor hasRanges={hasValues} onAdd={onAddValue} />
            )}
          </Stack>
        </CardContent>
      </Card>
    </Form>
  );
};
